import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { DesignerComponent } from "./designer/designer.component";
import { PageNotFoundComponent } from "./shared/page-not-found/page-not-found.component";
import { TrackOrderComponent } from "./shared/track-order/track-order.component";
import { WishlistPageComponent } from "./shared/wishlist-page/wishlist-page.component";
import { OrderSuccessComponent } from "./order-success/order-success.component";
import { HomePageComponent } from "./dashboard/home-page/home-page.component";
import { DashboardComponent } from "./dashboard/dashboard.component";
import { CartPageComponent } from "./dashboard/cart-page/cart-page.component";
import { ProductDetailPageComponent } from "./product-detail-page/product-detail-page.component";
import { UserDetailsComponent } from "./user-details/user-details.component";
import { ProfileComponent } from "./profile/profile.component";
import { PaymentComponent } from "./payment/payment.component";
import { TermConditionComponent } from "./shared/footer/term-condition/term-condition.component";
import { ContactComponent } from "./contact/contact.component";
import { AdminOrderPageComponent } from "./admin/admin-order-page/admin-order-page.component";
import { NoAccessComponent } from "./admin/no-access/no-access.component";
import { MyStructuresComponent } from "./my-structures/my-structures.component";
import { UserNotificationComponent } from "./user-notification/user-notification.component";
import { RewardsComponent } from "./rewards/rewards.component";
import { AuthGuard } from "./core/guards/auth.guard";
import { ProductViewlistComponent } from "./shared/product-viewlist/product-viewlist.component";
import { OverviewComponent } from "./profile/overview/overview.component";
import { AddressDetailsComponent } from "./profile/address-details/address-details.component";
import { ProfileDetailComponent } from "./profile/profile-details/profile-details.component";
import { UserReviewPageComponent } from "./profile/user-review-page/user-review-page.component";
import { RatingAndReviewComponent } from "./order-details/rating-and-review/rating-and-review.component";
import { ReviewSuccessComponent } from "./profile/review-success/review-success.component";
import { ItemListComponent } from "./profile/item-list/item-list.component";
import { ReturnReplacePageComponent } from "./profile/return-replace-page/return-replace-page.component";
import { OrderStatusComponent } from "./profile/order-status/order-status.component";
import { OrderInfoComponent } from "./profile/order-info/order-info.component";
import { AddressListComponent } from "./dashboard/checkout-page/address-list/address-list.component";
import { AddressFormComponent } from "./dashboard/checkout-page/address-form/address-form.component";
import { ProductReviewPageComponent } from "./product-detail-page/product-review-page/product-review-page.component";
import { OrderActionGuard } from "./core/guards/order-action.guard";
import { ProfileFormComponent } from './profile/profile-details/profile-form/profile-form.component';
import { AboutUsComponent } from "./shared/footer/about-us/about-us.component";
import { LoginPageComponent } from "./login-page/login-page.component";
import { noAuthGuard } from "./core/guards/no-auth.guard";
import { AuthLayoutComponent } from "./layouts/auth-layout/auth-layout.component";
import { UserLayoutComponent } from "./layouts/user-layout/user-layout.component";
import { AdminLayoutComponent } from "./layouts/admin-layout/admin-layout.component";
import { PlainLayoutComponent } from "./layouts/plain-layout/plain-layout.component";
import { PrivacyPageComponent } from './shared/footer/privacy-page/privacy-page.component';

const routes: Routes = [
    /* ================= ADMIN LAYOUT ================= */
    {
        path: "admin",
        component: AdminLayoutComponent,
        loadChildren: () => import("./admin/admin.module").then((m) => m.AdminModule),
    },

    /* ================= DEFAULT LAYOUT ================= */
    {
        path: "",
        component: UserLayoutComponent,
        children: [
            {
                path: "",
                pathMatch: "full",
                redirectTo: "home",
            },
            {
                path: "home",
                component: HomePageComponent,
                data: { noBackButton: true },
            },
            {
                path: "products/:id",
                component: ProductDetailPageComponent,
            },
            {
                path: "products/:id/reviews",
                component: ProductReviewPageComponent,
            },
            {
                path: "products",
                component: ProductViewlistComponent,
                data: { noHeader: true, noBackButton: true },
            },
            {
                path: "dashboard",
                component: DashboardComponent,
            },
            {
                path: "wishlist",
                component: WishlistPageComponent,
                canActivate: [AuthGuard],
            },
            {
                path: "cart",
                component: CartPageComponent,
            },
            {
                path: "address",
                component: AddressListComponent,
            },
            {
                path: "addAddress",
                component: AddressFormComponent,
            },
            {
                path: "editAddress/:id",
                component: AddressFormComponent,
            },
            {
                path: "payment",
                component: PaymentComponent,
                data: { noHeader: true },
            },
            {
                path: "order/success",
                component: OrderSuccessComponent,
            },
            {
                path: "order/success/:merchantTransactionId",
                component: OrderSuccessComponent,
            },
            {
                path: "adminOrderStatusHistory/:id",
                component: AdminOrderPageComponent,
            },
            {
                path: "noAccess",
                component: NoAccessComponent,
            },
            {
                path: "contact",
                component: ContactComponent,
            },
            {
                path: "notifications",
                component: UserNotificationComponent,
                canActivate: [AuthGuard],
            },
            {
                path: "terms-and-conditions",
                component: TermConditionComponent,
            },
            {
                path: "privacy-and-policy",
                component: PrivacyPageComponent,
            },
            {
                path: "about-us",
                component: AboutUsComponent,
            },
        ],
    },

    /* ================= AUTH LAYOUT WITH HEADER NO FOOTER ================= */
    {
        path: "",
        component: AuthLayoutComponent,
        children: [
            {
                path: "login",
                component: LoginPageComponent,
                canActivate: [noAuthGuard],
            },
        ],
    },
    /* ================= PLAIN LAYOUT WITH NO HEADER NO FOOTER ================= */
    {
        path: "",
        component: PlainLayoutComponent,
        children: [
            {
                path: "userDetails",
                component: UserDetailsComponent,
                canActivate: [AuthGuard],
            },
            {
                path: "designer",
                component: DesignerComponent,
            },
            {
                path: "designer/:id",
                component: DesignerComponent,
                canActivate: [AuthGuard],
            },
            {
                path: "account",
                component: ProfileComponent,
                canActivate: [AuthGuard],
                children: [
                    { path: "trackOrder", component: TrackOrderComponent },
                    { path: "overview", component: OverviewComponent },
                    { path: "rewards", component: RewardsComponent },
                    { path: "addressDetails", component: AddressDetailsComponent },
                    { path: "profileDetails", component: ProfileDetailComponent },
                    { path: "profileDetails/:id", component: ProfileFormComponent },
                    {
                        path: "orders/:id/itemsReview/:itemId/review",
                        component: RatingAndReviewComponent,
                    },
                    {
                        path: "orders/reviewSucccess/:id",
                        component: ReviewSuccessComponent,
                    },
                    {
                        path: "orders/:orderId/itemsReview",
                        component: UserReviewPageComponent,
                    },
                    {
                        path: "orders/:id/track",
                        component: OrderStatusComponent,
                    },
                    {
                        path: "orders/:id/orderInfo",
                        component: OrderInfoComponent,
                    },
                    {
                        path: "orders/:id/:action",
                        component: ItemListComponent,
                        canActivate: [OrderActionGuard],
                    },
                    {
                        path: "orders/:id/:action/reason",
                        component: ReturnReplacePageComponent,
                    },
                    {
                        path: "myStructures",
                        component: MyStructuresComponent,
                    },
                ],
            },
        ],
    },

    /* ================= GLOBAL FALLBACK ================= */
    {
        path: "page-not-found",
        component: PageNotFoundComponent,
    },
    {
        path: "**",
        redirectTo: "page-not-found",
    },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppRoutingModule {}
